import React, { useState, useEffect } from 'react';
import { Search, Eye, Edit, Trash2, UserX, UserCheck, Download, RefreshCw, ChevronLeft, ChevronRight, CheckCircle, XCircle, Plus, CreditCard, Banknote, Edit3, FileCheck } from 'lucide-react';
import axios from 'axios';
import AssignCreditModal from '../../components/AssignCreditModal';
import AgentKycModal from '../../components/admin/AgentKycModal';
import PhoneInput from '../../components/ui/PhoneInput';
import { agentKycHref } from '../../utils/fileUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AgentManagement = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAgents, setTotalAgents] = useState(0);
  
  // Modals
  const [viewModal, setViewModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [createModal, setCreateModal] = useState(false);
  const [assignCreditModal, setAssignCreditModal] = useState(null);
  const [kycModal, setKycModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editCreditModal, setEditCreditModal] = useState(null);
  const [editCreditForm, setEditCreditForm] = useState({ credit_limit: '', payment_day: 'MONDAY' });
  const [createFormData, setCreateFormData] = useState({
    agency_name: '',
    full_name: '',
    email: '',
    phone: '',
    password: '',
    wallet_balance: 0
  });
  
  // Stats
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, suspended: 0 });

  // Fetch agents
  const fetchAgents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/agents`, {
        withCredentials: true,
        params: {
          page,
          limit: 20,
          search: searchTerm || undefined,
          status: statusFilter || undefined
        }
      });
      
      setAgents(response.data.agents);
      setTotalPages(response.data.pages);
      setTotalAgents(response.data.total);
      
      // Calculate stats
      const activeCount = response.data.agents.filter(a => a.status === 'active').length;
      const pendingCount = response.data.agents.filter(a => a.status === 'pending_approval').length;
      const suspendedCount = response.data.agents.filter(a => a.status === 'suspended').length;
      setStats({ 
        total: response.data.total, 
        active: activeCount, 
        pending: pendingCount,
        suspended: suspendedCount 
      });
    } catch (error) {
      console.error('Error fetching agents:', error);
      alert('Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, [page, searchTerm, statusFilter]);

  // View agent details
  const viewAgentDetails = async (agentId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/agents/${agentId}`, {
        withCredentials: true
      });
      setViewModal(response.data);
    } catch (error) {
      console.error('Error fetching agent details:', error);
      alert('Failed to fetch agent details');
    }
  };

  // Open edit modal
  const openEditModal = (agent) => {
    setEditModal(agent);
    setEditFormData({
      agency_name: agent.agency_name,
      full_name: agent.full_name,
      email: agent.email,
      phone: agent.phone,
      status: agent.status,
      special_markup_enabled: agent.special_markup_enabled || false,
      commission_share_pct: agent.commission_share_pct ?? '',
    });
  };

  // Update agent
  const updateAgent = async () => {
    try {
      const { commission_share_pct: commPct, ...agentParams } = editFormData;
      await axios.put(
        `${BACKEND_URL}/api/admin/agents/${editModal.id}`,
        null,
        {
          withCredentials: true,
          params: agentParams,
        },
      );
      if (commPct !== '' && commPct != null) {
        await axios.patch(
          `${BACKEND_URL}/api/admin/agents/${editModal.id}/commission`,
          { commission_share_pct: parseFloat(commPct) || 0 },
          { withCredentials: true },
        );
      }

      alert('Agent updated successfully');
      setEditModal(null);
      fetchAgents();
    } catch (error) {
      console.error('Error updating agent:', error);
      alert(error.response?.data?.detail || 'Failed to update agent');
    }
  };

  // Create agent
  const createAgent = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/agents`,
        null,
        {
          withCredentials: true,
          params: createFormData
        }
      );
      
      alert('Agent created successfully');
      setCreateModal(false);
      setCreateFormData({
        agency_name: '',
        full_name: '',
        email: '',
        phone: '',
        password: '',
        wallet_balance: 0
      });
      fetchAgents();
    } catch (error) {
      console.error('Error creating agent:', error);
      alert(error.response?.data?.detail || 'Failed to create agent');
    }
  };

  // Approve agent
  const approveAgent = async (agentId) => {
    if (!confirm('Approve this agent account?')) return;
    
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/agents/${agentId}/approve`,
        null,
        { withCredentials: true }
      );
      
      alert('Agent approved successfully');
      fetchAgents();
    } catch (error) {
      console.error('Error approving agent:', error);
      alert('Failed to approve agent');
    }
  };

  const openRejectModal = (agent) => {
    setRejectModal(agent);
    setRejectReason('');
  };

  const rejectAgent = async () => {
    if (!rejectModal) return;

    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/agents/${rejectModal.id}/reject`,
        { reason: rejectReason.trim() || undefined },
        { withCredentials: true }
      );

      alert('Agent rejected successfully');
      setRejectModal(null);
      setRejectReason('');
      fetchAgents();
    } catch (error) {
      console.error('Error rejecting agent:', error);
      alert('Failed to reject agent');
    }
  };

  // Change agent status
  const changeStatus = async (agentId, newStatus) => {
    if (!confirm(`Are you sure you want to ${newStatus} this agent?`)) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/agents/${agentId}`,
        null,
        {
          withCredentials: true,
          params: { status: newStatus }
        }
      );
      
      alert(`Agent ${newStatus} successfully`);
      fetchAgents();
    } catch (error) {
      console.error('Error changing status:', error);
      alert('Failed to change agent status');
    }
  };

  // Delete agent
  const deleteAgent = async (agentId) => {
    if (!confirm('Are you sure you want to delete this agent? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/agents/${agentId}`, {
        withCredentials: true
      });
      
      alert('Agent deleted successfully');
      fetchAgents();
    } catch (error) {
      console.error('Error deleting agent:', error);
      alert('Failed to delete agent');
    }
  };

  // Open edit credit modal
  const openEditCredit = (agent) => {
    setEditCreditModal(agent);
    setEditCreditForm({
      credit_limit: agent.credit_limit || '',
      payment_day: agent.credit_payment_day || 'MONDAY'
    });
  };

  // Update agent credit settings
  const updateAgentCredit = async () => {
    if (!editCreditModal) return;
    const params = new URLSearchParams({
      credit_limit: editCreditForm.credit_limit,
      payment_day: editCreditForm.payment_day
    });
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/credit/update/${editCreditModal.id}?${params}`,
        null,
        { withCredentials: true }
      );
      alert('Credit updated successfully');
      setEditCreditModal(null);
      fetchAgents();
    } catch (error) {
      console.error('Error updating credit:', error);
      alert(error.response?.data?.detail || 'Failed to update credit');
    }
  };

  // Revert credit agent to cash mode
  const revertAgentToCash = async (agent) => {
    const used = Number(agent.credit_used || 0);
    if (used > 0) {
      alert(`Cannot revert — agent has outstanding credit usage of ₹${used.toLocaleString()}. Settle it first.`);
      return;
    }
    if (!confirm(`Revert ${agent.agency_name} back to CASH mode? This clears all credit settings.`)) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/credit/revert-to-cash/${agent.id}`,
        null,
        { withCredentials: true }
      );
      alert('Agent reverted to cash mode successfully');
      fetchAgents();
    } catch (error) {
      console.error('Error reverting to cash:', error);
      alert(error.response?.data?.detail || 'Failed to revert agent to cash');
    }
  };



  return (
    <>
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Agent Users Management</h1>
          <p className="text-gray-500">Manage travel agent accounts and approvals</p>
        </div>
        <button
          onClick={() => setCreateModal(true)}
          className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create Agent
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Total Agents</p>
              <p className="text-3xl font-bold text-gray-900">{totalAgents}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Active Agents</p>
              <p className="text-3xl font-bold text-green-600">{stats.active}</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Pending Approval</p>
              <p className="text-3xl font-bold text-orange-600">{stats.pending}</p>
            </div>
            <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Suspended</p>
              <p className="text-3xl font-bold text-red-600">{stats.suspended}</p>
            </div>
            <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
              <UserX className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by agency, name, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="suspended">Suspended</option>
            <option value="rejected">Rejected</option>
          </select>

          {/* Refresh */}
          <button
            onClick={fetchAgents}
            className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Agents Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Agent</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Payment Mode</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Balance/Credit</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Bookings</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Joined</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading agents...
                  </td>
                </tr>
              ) : agents.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No agents found
                  </td>
                </tr>
              ) : (
                agents.map((agent) => (
                  <tr key={agent.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-semibold text-gray-900">{agent.agency_name}</p>
                        <p className="text-sm text-gray-500">{agent.full_name}</p>
                        <p className="text-xs text-gray-400">ID: {agent.id.slice(0, 8)}...</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm text-gray-900">{agent.email}</p>
                        <p className="text-sm text-gray-500">{agent.phone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {agent.payment_mode === 'HYBRID' ? (
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold w-fit">
                          Wallet + Credit
                        </span>
                      ) : agent.payment_mode === 'CREDIT' ? (
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold flex items-center gap-1 w-fit">
                          <CreditCard className="w-3 h-3" />
                          Credit
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold w-fit">
                          💰 Cash
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {['CREDIT', 'HYBRID'].includes(agent.payment_mode) ? (
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            ₹{agent.credit_available?.toLocaleString() || 0} / ₹{agent.credit_limit?.toLocaleString() || 0}
                          </p>
                          <p className="text-xs text-gray-500">Credit avail / limit</p>
                          {agent.payment_mode === 'HYBRID' && (
                            <p className="text-xs text-gray-600 mt-1">Wallet: ₹{agent.wallet_balance?.toLocaleString() || 0}</p>
                          )}
                        </div>
                      ) : (
                        <span className="font-semibold text-gray-900">₹{agent.wallet_balance?.toLocaleString() || 0}</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-gray-900">{agent.total_bookings || 0}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        agent.status === 'active' ? 'bg-green-100 text-green-700' :
                        agent.status === 'pending_approval' ? 'bg-orange-100 text-orange-700' :
                        agent.status === 'suspended' ? 'bg-red-100 text-red-700' :
                        agent.status === 'rejected' ? 'bg-gray-100 text-gray-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {agent.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(agent.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {/* Assign Credit Button — only for CASH agents */}
                        {!['CREDIT', 'HYBRID'].includes(agent.payment_mode) && agent.status === 'active' && (
                          <button
                            onClick={() => setAssignCreditModal(agent)}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Assign Credit"
                            data-testid={`assign-credit-${agent.id}`}
                          >
                            <CreditCard className="w-5 h-5" />
                          </button>
                        )}

                        {/* Edit Credit + Revert to Cash — only for CREDIT agents */}
                        {['CREDIT', 'HYBRID'].includes(agent.payment_mode) && (
                          <>
                            <button
                              onClick={() => openEditCredit(agent)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="Edit Credit Limit / Payment Day"
                              data-testid={`edit-credit-${agent.id}`}
                            >
                              <Edit3 className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => revertAgentToCash(agent)}
                              disabled={Number(agent.credit_used || 0) > 0}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              title={Number(agent.credit_used || 0) > 0 ? `Cannot revert — outstanding ₹${Number(agent.credit_used).toLocaleString()}` : 'Switch to Cash Mode'}
                              data-testid={`revert-cash-${agent.id}`}
                            >
                              <Banknote className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        
                        <button
                          onClick={() => viewAgentDetails(agent.id)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        
                        {/* Approve/Reject for pending agents */}
                        {agent.status === 'pending_approval' && (
                          <>
                            <button
                              onClick={() => approveAgent(agent.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Approve Agent"
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => openRejectModal(agent)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Reject Agent"
                            >
                              <XCircle className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        
                        <button
                          onClick={() => openEditModal(agent)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit Agent"
                        >
                          <Edit className="w-5 h-5" />
                        </button>

                        {/* Tax / KYC — GSTIN + PAN + state */}
                        <button
                          onClick={() => setKycModal(agent)}
                          className={`p-2 rounded-lg transition-colors ${
                            agent.has_gstin ? 'text-emerald-700 hover:bg-emerald-50' : 'text-gray-600 hover:bg-gray-100'
                          }`}
                          title={agent.has_gstin ? `GSTIN: ${agent.gstin || '—'}` : 'Set KYC (GSTIN / PAN)'}
                          data-testid={`agent-kyc-btn-${agent.id}`}
                        >
                          <FileCheck className="w-5 h-5" />
                        </button>
                        
                        {/* Status Actions */}
                        {agent.status === 'active' ? (
                          <button
                            onClick={() => changeStatus(agent.id, 'suspended')}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Suspend Agent"
                          >
                            <UserX className="w-5 h-5" />
                          </button>
                        ) : agent.status === 'suspended' ? (
                          <button
                            onClick={() => changeStatus(agent.id, 'active')}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Activate Agent"
                          >
                            <UserCheck className="w-5 h-5" />
                          </button>
                        ) : null}
                        
                        <button
                          onClick={() => deleteAgent(agent.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Agent"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {agents.length} of {totalAgents} agents
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="px-4 py-2 text-sm font-medium">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Agent Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setCreateModal(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Create New Agent</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Agency Name</label>
                  <input
                    type="text"
                    value={createFormData.agency_name}
                    onChange={(e) => setCreateFormData({...createFormData, agency_name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                  <input
                    type="text"
                    value={createFormData.full_name}
                    onChange={(e) => setCreateFormData({...createFormData, full_name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={createFormData.email}
                  onChange={(e) => setCreateFormData({...createFormData, email: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                <PhoneInput
                  value={createFormData.phone}
                  onChange={(v) => setCreateFormData({...createFormData, phone: v})}
                  defaultCountry="IN"
                  testid="agent-create-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={createFormData.password}
                  onChange={(e) => setCreateFormData({...createFormData, password: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Initial Wallet Balance (₹)</label>
                <input
                  type="number"
                  value={createFormData.wallet_balance}
                  onChange={(e) => setCreateFormData({...createFormData, wallet_balance: parseFloat(e.target.value) || 0})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setCreateModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={createAgent}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Create Agent
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal - Similar to UserManagement */}
      {viewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setViewModal(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Agent Details</h2>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Agency Name</p>
                  <p className="font-semibold text-gray-900">{viewModal.agency_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Full Name</p>
                  <p className="font-semibold text-gray-900">{viewModal.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="font-semibold text-gray-900">{viewModal.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Phone</p>
                  <p className="font-semibold text-gray-900">{viewModal.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Status</p>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    viewModal.status === 'active' ? 'bg-green-100 text-green-700' :
                    viewModal.status === 'pending_approval' ? 'bg-orange-100 text-orange-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {viewModal.status.replace('_', ' ')}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Wallet Balance</p>
                  <p className="font-semibold text-gray-900">₹{viewModal.wallet_balance?.toLocaleString() || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Bookings</p>
                  <p className="font-semibold text-gray-900">{viewModal.total_bookings || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Commission</p>
                  <p className="font-semibold text-gray-900">₹{viewModal.total_commission?.toLocaleString() || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Commission share %</p>
                  <p className="font-semibold text-gray-900">
                    {viewModal.commission_share_pct != null ? `${viewModal.commission_share_pct}%` : 'Default (GST settings)'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Joined</p>
                  <p className="font-semibold text-gray-900">{new Date(viewModal.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">GST / KYC</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">PAN Card</p>
                    {viewModal.pan_card_url ? (
                      <a
                        href={agentKycHref(viewModal.pan_card_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-[#659A9B] hover:underline"
                      >
                        View document
                      </a>
                    ) : (
                      <p className="font-semibold text-gray-900">—</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">GST Certificate</p>
                    {viewModal.gst_certificate_url ? (
                      <a
                        href={agentKycHref(viewModal.gst_certificate_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-[#659A9B] hover:underline"
                      >
                        View document
                      </a>
                    ) : (
                      <p className="font-semibold text-gray-900">—</p>
                    )}
                  </div>
                </div>
                {viewModal.has_gstin ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">GSTIN</p>
                      <p className="font-semibold text-gray-900 font-mono">{viewModal.gstin || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">PAN</p>
                      <p className="font-semibold text-gray-900 font-mono">{viewModal.pan || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Billing State</p>
                      <p className="font-semibold text-gray-900">
                        {viewModal.state ? `${viewModal.state} (${viewModal.state_code || ''})` : '—'}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500 mb-1">Billing Address</p>
                      <p className="font-semibold text-gray-900 whitespace-pre-wrap">{viewModal.billing_address || '—'}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">Walk-in mode — no GSTIN on file.</p>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setViewModal(null)}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject confirmation modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setRejectModal(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Reject Application</h2>
              <p className="text-sm text-gray-600 mt-1">
                Reject {rejectModal.agency_name}? The applicant will be notified by email.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Incomplete documentation"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setRejectModal(null)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={rejectAgent}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Reject Application
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal - Similar to UserManagement */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditModal(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Edit Agent</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Agency Name</label>
                <input
                  type="text"
                  value={editFormData.agency_name}
                  onChange={(e) => setEditFormData({...editFormData, agency_name: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={editFormData.full_name}
                  onChange={(e) => setEditFormData({...editFormData, full_name: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                <PhoneInput
                  value={editFormData.phone}
                  onChange={(v) => setEditFormData({...editFormData, phone: v})}
                  defaultCountry="IN"
                  testid="agent-edit-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Commission share % (supplier gross)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  placeholder="Leave blank for GST default"
                  value={editFormData.commission_share_pct}
                  onChange={(e) => setEditFormData({ ...editFormData, commission_share_pct: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="agent-commission-share-pct"
                />
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editFormData.special_markup_enabled}
                    onChange={(e) => setEditFormData({...editFormData, special_markup_enabled: e.target.checked})}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span className="text-sm font-semibold text-gray-700">Enable Special Markup</span>
                </label>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setEditModal(null)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={updateAgent}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
      
      {/* Assign Credit Modal */}
      {assignCreditModal && (
        <AssignCreditModal
          agent={assignCreditModal}
          onClose={() => setAssignCreditModal(null)}
          onSuccess={() => {
            fetchAgents();
            setAssignCreditModal(null);
          }}
        />
      )}

      {/* KYC Modal — GSTIN / PAN / State */}
      {kycModal && (
        <AgentKycModal
          agent={kycModal}
          onClose={() => setKycModal(null)}
          onSaved={() => {
            fetchAgents();
            setKycModal(null);
          }}
        />
      )}

      {/* Edit Credit Modal */}
      {editCreditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-credit-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Edit Credit Settings</h2>
                <p className="text-sm text-gray-500 mt-0.5">{editCreditModal.agency_name} ({editCreditModal.full_name})</p>
              </div>
              <button onClick={() => setEditCreditModal(null)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Current Limit</span>
                <span className="font-semibold">₹{Number(editCreditModal.credit_limit || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-gray-600">Currently Used</span>
                <span className="font-semibold">₹{Number(editCreditModal.credit_used || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-gray-600">Available</span>
                <span className="font-semibold">₹{Number(editCreditModal.credit_available || 0).toLocaleString()}</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Credit Limit (₹)</label>
                <input
                  type="number"
                  min={Number(editCreditModal.credit_used || 0)}
                  value={editCreditForm.credit_limit}
                  onChange={(e) => setEditCreditForm({ ...editCreditForm, credit_limit: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  data-testid="edit-credit-limit-input"
                />
                <p className="text-xs text-gray-500 mt-1">Must be &ge; currently used ₹{Number(editCreditModal.credit_used || 0).toLocaleString()}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Day</label>
                <select
                  value={editCreditForm.payment_day}
                  onChange={(e) => setEditCreditForm({ ...editCreditForm, payment_day: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  data-testid="edit-credit-payday-select"
                >
                  {['MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY','SUNDAY'].map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setEditCreditModal(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={updateAgentCredit}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                data-testid="edit-credit-save-btn"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgentManagement;
